/**
 * 管理后台「运行与安全 A」文案（韩、中、英）：推理队列、请求日志、生图记录、搜索记录，以及日期时间选择器与导出对话框。
 *
 * 背景：页面按 Alice 的版式复刻，文案保持 Alice 的措辞；品牌与数据来源按本仓适配：
 *   货币单位叫「花瓣」（不出现糖豆）、计费主体是账号、没有审查拦截与 IP 归属地、没有联网搜索接口。
 * 设计意图：按页面分组；后端新增的错误码在这里映射（common.ts 的错误表归共用文件，不在这里改），
 *   没映射到的回落到共用表。
 * 关键约束：韩文用 해요체，不逐字直译中文；中文不用破折号和英文引号。
 */
import { errorText as baseErrorText } from './common.ts'
import { translate, type Entry, type Locale } from './core.tsx'

const e = (zh: string, ko: string, en: string): Entry => ({ zh, ko, en })

const CODES: Record<string, Entry> = {
  invalid_pool: e('任務類型篩選值不合法', '작업 유형 필터 값이 올바르지 않아요', 'Invalid task type filter'),
  invalid_status: e('狀態篩選值不合法', '상태 필터 값이 올바르지 않아요', 'Invalid status filter'),
  invalid_range: e('起始時間不能晚於截止時間', '시작 시각이 종료 시각보다 늦을 수 없어요', 'Start must not be later than end'),
  invalid_time: e('時間格式不合法', '시간 형식이 올바르지 않아요', 'Invalid time format'),
  invalid_date: e('日期格式不合法', '날짜 형식이 올바르지 않아요', 'Invalid date format'),
  invalid_dimension: e('模型口徑不合法', '모델 기준 값이 올바르지 않아요', 'Invalid model dimension'),
  invalid_format: e('只支援匯出 JSONL', 'JSONL 형식만 내보낼 수 있어요', 'Only JSONL export is supported'),
  invalid_org: e('使用者 ID 需要是帳號 UUID', '사용자 ID는 계정 UUID여야 해요', 'The user ID must be an account UUID'),
  dashboard_range_too_long: e('未指定使用者時，看板最長統計 31 天', '사용자를 지정하지 않으면 대시보드는 최대 31일까지 볼 수 있어요', 'Without a user, the dashboard covers at most 31 days'),
  range_too_long: e('單次最多 31 天，請按月分批下載', '한 번에 최대 31일까지 받을 수 있어요. 월 단위로 나눠 받아 주세요', 'One export covers at most 31 days'),
  preview_unavailable: e('這條記錄沒有保留可預覽的圖片', '이 기록에는 미리 볼 수 있는 이미지가 남아 있지 않아요', 'No retained image to preview'),
  preview_not_image: e('上遊返回的內容無法識別為圖片，已拒絕預覽', '상류가 이미지가 아닌 내용을 돌려줘서 미리보기를 막았어요', 'The upstream content is not an image, preview refused'),
  not_found: e('找不到這條記錄', '기록을 찾을 수 없어요', 'Record not found'),
}

/** 本组页面的错误提示：先查本组新增的错误码，其余沿用共用错误表。 */
export function errorTextRuntime(err: unknown, locale: Locale): string {
  const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : ''
  const entry = CODES[code]
  return entry ? translate(entry, locale) : baseErrorText(err, locale)
}

export const AG_runtime = {
  common: {
    filter: e('篩選', '필터', 'Filter'),
    clear: e('清空', '초기화', 'Clear'),
    loading: e('載入中...', '불러오는 중이에요...', 'Loading...'),
    loadFailed: e('載入失敗：{msg}', '불러오지 못했어요: {msg}', 'Load failed: {msg}'),
    retry: e('重試', '다시 시도', 'Retry'),
    cancel: e('取消', '취소', 'Cancel'),
    close: e('關閉', '닫기', 'Close'),
    prev: e('上一頁', '이전', 'Previous'),
    next: e('下一頁', '다음', 'Next'),
    pageOf: e('第 {p} / {n} 頁', '{p} / {n} 페이지', 'Page {p} of {n}'),
    pageN: e('第 {n} 頁', '{n} 페이지', 'Page {n}'),
    totalRows: e('共 {n} 條', '총 {n}건', '{n} rows'),
    to: e('至', '~', 'to'),
    detail: e('詳情', '상세', 'Details'),
    copied: e('已複製', '복사했어요', 'Copied'),
    exportJson: e('匯出 JSON', 'JSON 내보내기', 'Export JSON'),
    exported: e('已匯出', '내보냈어요', 'Exported'),
    petals: e('花瓣', '꽃잎', 'petals'),
    petalsValue: e('{n} 花瓣', '{n} 꽃잎', '{n} petals'),
    adminOnly: e('匯出需要管理員角色', '내보내기는 관리자 권한이 필요해요', 'Export requires the admin role'),
    accountFallback: e('帳號 {id}', '계정 {id}', 'Account {id}'),
    yes: e('是', '예', 'Yes'),
    no: e('否', '아니요', 'No'),
  },

  queue: {
    eyebrow: e('LIVE INFERENCE LOAD', 'LIVE INFERENCE LOAD', 'LIVE INFERENCE LOAD'),
    title: e('推理隊列', '추론 대기열', 'Inference queue'),
    desc: e('實時查看 LLM 與圖片任務的運行載荷、排隊深度和等待時間', 'LLM과 이미지 작업의 실행 부하, 대기 깊이, 대기 시간을 실시간으로 확인해요', 'Live load, queue depth and wait time for LLM and image jobs'),
    refreshState: e('每 3 秒自動刷新 · {time}', '3초마다 자동 새로고침 · {time}', 'Auto refresh every 3s · {time}'),
    llmRunning: e('LLM 運行載荷', 'LLM 실행 부하', 'LLM running load'),
    llmQueued: e('LLM 排隊', 'LLM 대기', 'LLM queued'),
    imageRunning: e('圖片運行載荷', '이미지 실행 부하', 'Image running load'),
    imageQueued: e('圖片排隊', '이미지 대기', 'Image queued'),
    capacitySub: e('當前運行 / 並行上限 · 近 1h 平均等待 {avg}', '실행 중 / 동시 처리 한도 · 최근 1시간 평균 대기 {avg}', 'Running / concurrency cap · avg wait in last 1h {avg}'),
    oldestWait: e('最久等待 {v}', '가장 오래 대기 {v}', 'Longest wait {v}'),
    configTitle: e('準入規則', '수용 규칙', 'Admission rules'),
    configCopy: e(
      '本網關用 Redis 準入：渠道並行租約加優先級讓路，下面是當前生效的讀數。並行上限在渠道與 API Key 上設定，這裡不提供修改',
      '이 게이트웨이는 Redis로 요청을 받아요. 채널 동시 처리 임대와 우선순위 양보로 동작하고, 아래는 지금 적용 중인 값이에요. 동시 처리 한도는 채널과 API 키에서 설정하므로 여기서는 바꿀 수 없어요',
      'The gateway admits requests through Redis: channel concurrency leases plus priority yielding. These are the live values. Concurrency caps are set on channels and API keys, not here',
    ),
    priorityRule: e('{id}：最多佔渠道並行的 {cap}%，擁塞時最長等待 {wait} 秒', '{id}: 채널 동시 처리의 최대 {cap}%, 혼잡할 때 최대 {wait}초 대기', '{id}: up to {cap}% of channel concurrency, waits at most {wait}s when congested'),
    priorityNoWait: e('{id}：最多佔渠道並行的 {cap}%，擁塞時不排隊直接拒絕', '{id}: 채널 동시 처리의 최대 {cap}%, 혼잡하면 대기 없이 바로 거절', '{id}: up to {cap}% of channel concurrency, rejected immediately when congested'),
    windowRule: e('已完成、失敗與取消的任務取最近 {n} 分鐘', '완료, 실패, 취소된 작업은 최근 {n}분 기준이에요', 'Completed, failed and cancelled jobs cover the last {n} minutes'),
    capRule: e('並行上限是啟用渠道的並行之和', '동시 처리 한도는 사용 중인 채널의 동시 처리 합계예요', 'The cap is the sum of enabled channel concurrency'),
    allTypes: e('全部類型', '전체 유형', 'All types'),
    llm: e('LLM', 'LLM', 'LLM'),
    image: e('圖片', '이미지', 'Image'),
    allStatus: e('全部狀態', '전체 상태', 'All statuses'),
    queued: e('排隊中', '대기 중', 'Queued'),
    running: e('運行中', '실행 중', 'Running'),
    completed: e('已完成', '완료', 'Completed'),
    failed: e('失敗', '실패', 'Failed'),
    cancelled: e('已取消', '취소됨', 'Cancelled'),
    refreshNow: e('立即刷新', '지금 새로고침', 'Refresh now'),
    totalBadge: e('共 {n} 條 · 隊列運行中', '총 {n}건 · 대기열 동작 중', '{n} rows · queue active'),
    colStatus: e('狀態', '상태', 'Status'),
    colKind: e('類型', '유형', 'Type'),
    colQueuedAt: e('入隊時間', '대기열 진입', 'Queued at'),
    colUser: e('使用者', '사용자', 'User'),
    colModel: e('模型', '모델', 'Model'),
    colWait: e('等待', '대기', 'Wait'),
    colRun: e('運行', '실행', 'Run'),
    colRequest: e('Request ID', 'Request ID', 'Request ID'),
    colNode: e('節點', '노드', 'Node'),
    colError: e('錯誤', '오류', 'Error'),
    empty: e('暫無隊列記錄', '대기열 기록이 없어요', 'No queue records'),
  },

  logs: {
    eyebrow: e('REQUEST OBSERVABILITY', 'REQUEST OBSERVABILITY', 'REQUEST OBSERVABILITY'),
    title: e('請求日誌', '요청 로그', 'Request logs'),
    desc: e('檢索每一次模型呼叫的路由、時延、Token、費用與響應狀態', '모델 호출마다 라우팅, 지연 시간, 토큰, 비용, 응답 상태를 찾아봐요', 'Search the route, latency, tokens, cost and status of every model call'),
    viewTabs: e('請求日誌視圖', '요청 로그 보기', 'Request log views'),
    viewList: e('呼叫明細', '호출 내역', 'Calls'),
    viewDashboard: e('用量看板', '사용량 대시보드', 'Usage dashboard'),
    phUser: e('按帳號或 Key ID...', '계정 또는 키 ID로 찾기...', 'Account or key ID...'),
    phModel: e('按模型...', '모델로 찾기...', 'Model...'),
    phSource: e('按來源...', '출처로 찾기...', 'Source...'),
    start: e('起始', '시작', 'From'),
    end: e('截止', '종료', 'To'),
    startAria: e('起始時間，24 小時制', '시작 시각, 24시간제', 'Start time, 24-hour'),
    endAria: e('截止時間，24 小時制', '종료 시각, 24시간제', 'End time, 24-hour'),
    sizeBadge: e('佔用 {gb} GB', '용량 {gb} GB', '{gb} GB used'),
    exportJsonl: e('匯出 JSONL', 'JSONL 내보내기', 'Export JSONL'),
    exportTip: e('快速匯出最多 10,000 條', '최대 10,000건까지 빠르게 내보내요', 'Quick export, up to 10,000 rows'),
    vendor: e('下載歷史對話', '과거 대화 내려받기', 'Download conversations'),
    vendorTip: e('下載保留期內、帳號允許廠商匯出的使用者與助手文字訓練對', '보관 기간 안에 있고 계정이 공급사 내보내기를 허용한 사용자와 어시스턴트 대화쌍을 받아요', 'Text pairs from retained bodies of accounts that allow vendor export'),
    badTime: e('{label}請按 YYYY-MM-DD HH:mm:ss 選擇', '{label}은(는) YYYY-MM-DD HH:mm:ss 형식으로 골라 주세요', '{label}: choose YYYY-MM-DD HH:mm:ss'),
    badRange: e('起始時間不能晚於截止時間', '시작 시각이 종료 시각보다 늦을 수 없어요', 'Start must not be later than end'),
    sumRequests: e('請求總數', '전체 요청', 'Requests'),
    sumStatus: e('{ok} 成功 · {fail} 失敗', '성공 {ok} · 실패 {fail}', '{ok} ok · {fail} failed'),
    sumTokens: e('總 Token', '전체 토큰', 'Total tokens'),
    sumTokenDetail: e('輸入 {i} · 輸出 {o} · 快取 {c}', '입력 {i} · 출력 {o} · 캐시 {c}', 'In {i} · out {o} · cached {c}'),
    sumCharge: e('使用者實扣', '사용자 실제 차감', 'Charged to users'),
    sumBilled: e('{n} 筆成功帳單 · 按請求去重', '정산된 {n}건 · 요청 기준 중복 제거', '{n} billed requests · deduplicated'),
    sumUpstream: e('已知上遊成本', '확인된 상류 원가', 'Known upstream cost'),
    sumCoverage: e('{cov} · {n} 筆成本未知', '{cov} · 원가 미확인 {n}건', '{cov} · {n} unknown'),
    coverage: e('{p} 覆蓋', '{p} 확인', '{p} covered'),
    noBilled: e('暫無成功帳單', '정산된 요청이 없어요', 'No billed requests'),
    needUser: e('填寫使用者 ID 後顯示該使用者的匯總', '사용자 ID를 입력하면 요약이 보여요', 'Enter a user ID to see its summary'),
    colTime: e('時間', '시각', 'Time'),
    colUser: e('使用者', '사용자', 'User'),
    colModel: e('供應商/模型', '공급사/모델', 'Provider/model'),
    colEndpoint: e('端點', '엔드포인트', 'Endpoint'),
    colSource: e('來源', '출처', 'Source'),
    colDuration: e('耗時', '소요 시간', 'Duration'),
    colInput: e('輸入', '입력', 'Input'),
    colOutput: e('輸出', '출력', 'Output'),
    colCached: e('快取', '캐시', 'Cached'),
    colCharge: e('使用者實扣', '사용자 차감', 'Charged'),
    colUpstream: e('上遊成本', '상류 원가', 'Upstream cost'),
    colStatus: e('狀態', '상태', 'Status'),
    empty: e('暫無日誌', '로그가 없어요', 'No logs'),
    sourceTip: e('{s} · 點選篩選', '{s} · 눌러서 필터', '{s} · click to filter'),
    listPrice: e('原價 {v}', '정가 {v}', 'List {v}'),
    paid: e('實扣 {v}', '차감 {v}', 'Charged {v}'),
    discountOf: e('優惠：{code}', '할인: {code}', 'Discount: {code}'),
    subsidy: e('平臺補貼', '플랫폼 지원', 'Platform subsidy'),
    waited: e('排隊 {v}', '대기 {v}', 'queued {v}'),
  },

  dash: {
    waiting: e('等待資料', '데이터를 기다려요', 'Waiting for data'),
    aggregating: e('正在聚合…', '집계하는 중이에요…', 'Aggregating…'),
    scope: e('統計區間 {start} 至 {end} · {bucket} · {n} 個{dim}', '집계 구간 {start} ~ {end} · {bucket} · {dim} {n}개', 'Window {start} to {end} · {bucket} · {n} {dim}'),
    bucketHour: e('按小時', '시간별', 'hourly'),
    bucketDay: e('按天', '일별', 'daily'),
    dimShell: e('對外模型', '대외 모델', 'public models'),
    dimUpstream: e('上遊路由', '상류 라우트', 'upstream routes'),
    defaultWeek: e('未設起始時間，預設近 7 天', '시작 시각이 없어 최근 7일로 봐요', 'No start set, last 7 days'),
    defaultMonth: e('未設起始時間，預設近 30 天', '시작 시각이 없어 최근 30일로 봐요', 'No start set, last 30 days'),
    noUserLimit: e('未指定使用者時最長 31 天', '사용자를 지정하지 않으면 최대 31일이에요', 'Up to 31 days without a user'),
    dimLabel: e('模型口徑', '모델 기준', 'Model basis'),
    dimShellTip: e('按使用者請求的模型聚合', '사용자가 요청한 모델 기준으로 집계해요', 'Aggregate by the model the user asked for'),
    dimUpstreamTip: e('按實際路由到的渠道與上遊模型聚合', '실제로 라우팅된 채널과 상류 모델 기준으로 집계해요', 'Aggregate by the channel and upstream model actually used'),
    metricLabel: e('趨勢指標', '추세 지표', 'Trend metric'),
    metricRequests: e('請求', '요청', 'Requests'),
    metricTokens: e('Token', '토큰', 'Tokens'),
    metricCost: e('使用者實扣', '사용자 차감', 'Charged'),
    unitRequests: e('次', '회', 'calls'),
    unitTokens: e('Token', '토큰', 'tokens'),
    unitCost: e('花瓣', '꽃잎', 'petals'),
    kpiRequests: e('請求數', '요청 수', 'Requests'),
    kpiRequestsSub: e('成功率 {r} · 失敗 {f} · {u} 位使用者', '성공률 {r} · 실패 {f} · 사용자 {u}명', 'Success {r} · failed {f} · {u} users'),
    kpiTokens: e('總 Token', '전체 토큰', 'Total tokens'),
    kpiTokensSub: e('輸入 {i} · 輸出 {o} · 快取命中 {c}', '입력 {i} · 출력 {o} · 캐시 적중 {c}', 'In {i} · out {o} · cache hit {c}'),
    kpiCharge: e('使用者實扣', '사용자 실제 차감', 'Charged to users'),
    kpiChargeSub: e('{n} 筆成功帳單 · 按請求去重', '정산된 {n}건 · 요청 기준 중복 제거', '{n} billed requests · deduplicated'),
    kpiUpstream: e('已知上遊成本', '확인된 상류 원가', 'Known upstream cost'),
    kpiUpstreamSub: e('毛利率 {m} · 成本覆蓋 {c}', '마진율 {m} · 원가 확인 {c}', 'Margin {m} · cost coverage {c}'),
    trendTitle: e('{metric}趨勢按模型', '모델별 {metric} 추세', '{metric} trend by model'),
    trendHint: e('{bucket} · {unit} · {tz}', '{bucket} · {unit} · {tz}', '{bucket} · {unit} · {tz}'),
    hourly: e('每小時', '시간마다', 'Hourly'),
    daily: e('每天', '매일', 'Daily'),
    shareTitle: e('模型佔比', '모델 비중', 'Model share'),
    shareHint: e('按{metric}', '{metric} 기준', 'By {metric}'),
    statusTitle: e('成功與失敗', '성공과 실패', 'Success and failure'),
    statusHint: e('狀態碼 ≥ 400 計為失敗', '상태 코드 400 이상은 실패로 세요', 'Status code 400 or above counts as failed'),
    other: e('其他', '기타', 'Other'),
    success: e('成功', '성공', 'Success'),
    failed: e('失敗', '실패', 'Failed'),
    failRate: e('失敗率: {r}', '실패율: {r}', 'Failure rate: {r}'),
    totalLabel: e('合計: {v}', '합계: {v}', 'Total: {v}'),
    modelsTitle: e('模型用量明細', '모델 사용량 내역', 'Model usage'),
    modelsHint: e('點選表頭排序 · 點選「看明細」切到呼叫明細', '표 머리글을 눌러 정렬해요 · 「내역 보기」를 누르면 호출 내역으로 이동해요', 'Click a header to sort · click View to open the call list'),
    colModel: e('模型', '모델', 'Model'),
    colUpstreamRoute: e('上遊路由', '상류 라우트', 'Upstream route'),
    colRequests: e('請求', '요청', 'Requests'),
    colSuccessRate: e('成功率', '성공률', 'Success rate'),
    colUsers: e('使用者', '사용자', 'Users'),
    colInput: e('輸入', '입력', 'Input'),
    colOutput: e('輸出', '출력', 'Output'),
    colCacheRate: e('快取命中', '캐시 적중', 'Cache hit'),
    colCharge: e('使用者實扣', '사용자 차감', 'Charged'),
    colUpstream: e('上遊成本', '상류 원가', 'Upstream cost'),
    colMargin: e('毛利率', '마진율', 'Margin'),
    colAvg: e('平均時延', '평균 지연', 'Avg latency'),
    colP95: e('P95', 'P95', 'P95'),
    viewDetail: e('看明細', '내역 보기', 'View'),
    emptyModels: e('當前篩選下沒有呼叫', '현재 필터에는 호출이 없어요', 'No calls under this filter'),
    coverageShort: e('覆蓋 {p}', '확인 {p}', 'cover {p}'),
    sourcesTitle: e('來源分布', '출처 분포', 'Sources'),
    sourcesHint: e('按請求數 · 點選篩選', '요청 수 기준 · 눌러서 필터', 'By requests · click to filter'),
    usersTitle: e('高用量帳號', '사용량 많은 계정', 'Top accounts'),
    usersHint: e('按使用者實扣 · 點選篩選', '사용자 차감 기준 · 눌러서 필터', 'By charge · click to filter'),
    rankMeta: e('{n} 次 · {t} Token · 失敗 {f}', '{n}회 · 토큰 {t} · 실패 {f}', '{n} calls · {t} tokens · {f} failed'),
    noData: e('暫無資料', '데이터가 없어요', 'No data'),
    mainSource: e('主對話', '메인 대화', 'Main chat'),
  },

  datetime: {
    weekdays: e('日,一,二,三,四,五,六', '일,월,화,수,목,금,토', 'Su,Mo,Tu,We,Th,Fr,Sa'),
    monthTitle: e('{y} 年 {m} 月', '{y}년 {m}월', '{y}-{m}'),
    prevMonth: e('上個月', '이전 달', 'Previous month'),
    nextMonth: e('下個月', '다음 달', 'Next month'),
    hour: e('小時', '시', 'Hour'),
    minute: e('分鐘', '분', 'Minute'),
    second: e('秒', '초', 'Second'),
    clear: e('清空', '지우기', 'Clear'),
    now: e('現在', '지금', 'Now'),
    ok: e('確定', '확인', 'OK'),
  },

  exportDialog: {
    logsTitle: e('匯出請求日誌', '요청 로그 내보내기', 'Export request logs'),
    imagesTitle: e('匯出生圖記錄', '이미지 기록 내보내기', 'Export image records'),
    startDate: e('起始日期', '시작 날짜', 'Start date'),
    endDate: e('截止日期', '종료 날짜', 'End date'),
    maxRows: e('最大條數', '최대 건수', 'Max rows'),
    rows: e('{n} 條', '{n}건', '{n} rows'),
    withBodies: e('同時匯出保留期內的正文', '보관 기간 안의 본문도 함께 내보내기', 'Include retained bodies'),
    withBodiesHint: e('讀取正文會寫入審計記錄，沒有保存正文的請求不帶正文', '본문을 읽으면 감사 기록이 남아요. 본문을 저장하지 않은 요청에는 본문이 없어요', 'Reading bodies is audited. Requests without retained bodies have none'),
    submit: e('匯出 JSONL', 'JSONL 내보내기', 'Export JSONL'),
    working: e('匯出中…', '내보내는 중…', 'Exporting…'),
    done: e('匯出成功', '내보냈어요', 'Export done'),
    truncated: e('已匯出 {rows} / {matched} 條，超出的部分請縮小時間範圍後分批匯出', '{matched}건 중 {rows}건을 내보냈어요. 나머지는 기간을 줄여서 나눠 받아 주세요', 'Exported {rows} of {matched} rows. Narrow the time range to export the rest'),
    needRange: e('請選擇日期範圍', '날짜 범위를 골라 주세요', 'Choose a date range'),
    badRange: e('起始日期不能晚於截止日期', '시작 날짜가 종료 날짜보다 늦을 수 없어요', 'Start date must not be later than end date'),
    current: e('沿用當前篩選', '현재 필터 그대로 사용', 'Using the current filters'),
  },

  vendor: {
    title: e('下載歷史對話', '과거 대화 내려받기', 'Download conversations'),
    copy: e(
      '只匯出帳號策略允許廠商匯出、正文仍在保留期內的成功聊天，輸出裡沒有帳號、請求號等身份資訊。選擇日期和條數即可下載',
      '계정 정책이 공급사 내보내기를 허용하고 본문이 보관 기간 안에 있는 성공한 채팅만 내보내요. 계정, 요청 번호 같은 신원 정보는 들어가지 않아요. 날짜와 건수를 골라 받아 주세요',
      'Only successful chats from accounts that allow vendor export and whose bodies are still retained. No account or request identifiers are included',
    ),
    maxPairs: e('最大訓練對（1–2,000,000）', '최대 학습 쌍 (1–2,000,000)', 'Max pairs (1–2,000,000)'),
    inherit: e('沿用當前篩選：{user}{model}', '현재 필터 적용: {user}{model}', 'Using current filters: {user}{model}'),
    userOf: e('使用者 {id}', '사용자 {id}', 'user {id}'),
    allUsers: e('全部使用者', '전체 사용자', 'all users'),
    modelContains: e(' · 模型包含 {m}', ' · 모델에 {m} 포함', ' · model contains {m}'),
    submit: e('開始下載 JSONL.gz', 'JSONL.gz 내려받기 시작', 'Download JSONL.gz'),
    needRange: e('請選擇日期範圍', '날짜 범위를 골라 주세요', 'Choose a date range'),
    badRange: e('起始日期不能晚於截止日期', '시작 날짜가 종료 날짜보다 늦을 수 없어요', 'Start date must not be later than end date'),
    tooLong: e('單次最多 31 天，請按月分批下載', '한 번에 최대 31일까지 받을 수 있어요. 월 단위로 나눠 받아 주세요', 'One export covers at most 31 days'),
    badLimit: e('最大訓練對請填寫 1–2,000,000 之間的整數', '최대 학습 쌍은 1–2,000,000 사이 정수로 입력해 주세요', 'Max pairs must be an integer between 1 and 2,000,000'),
    done: e('下載完成', '내려받기를 마쳤어요', 'Download finished'),
  },

  detail: {
    title: e('請求詳情', '요청 상세', 'Request details'),
    requestBody: e('Request Body', 'Request Body', 'Request Body'),
    responseBody: e('Response Body', 'Response Body', 'Response Body'),
    none: e('（無）', '(없음)', '(none)'),
    loading: e('載入中…', '불러오는 중…', 'Loading…'),
    failed: e('載入失敗：{msg}', '불러오지 못했어요: {msg}', 'Load failed: {msg}'),
    notRetained: e(
      '這條請求的正文已超過保存期限被清理，或這條請求產生在統一保存正文之前',
      '이 요청의 본문은 보관 기간이 지나 정리됐거나, 본문을 일괄 저장하기 전에 만들어진 기록이에요',
      'The body is gone: it expired and was purged, or the request predates universal body retention',
    ),
    retainedUntil: e('正文保留至 {t}，讀取已寫入審計記錄', '본문은 {t}까지 보관돼요. 읽은 기록은 감사 로그에 남아요', 'Body kept until {t}. This read is recorded in the audit log'),
    errorPrefix: e('[ERROR] {msg}', '[ERROR] {msg}', '[ERROR] {msg}'),
    fRequestId: e('Request ID', 'Request ID', 'Request ID'),
    fAccountName: e('帳號', '계정', 'Account'),
    fAccountId: e('帳號 ID', '계정 ID', 'Account ID'),
    fKey: e('API Key', 'API 키', 'API key'),
    fEndUser: e('終端使用者', '최종 사용자', 'End user'),
    fChannel: e('渠道', '채널', 'Channel'),
    fModel: e('對外模型', '대외 모델', 'Public model'),
    fUpstreamModel: e('上遊模型', '상류 모델', 'Upstream model'),
    fEndpoint: e('端點', '엔드포인트', 'Endpoint'),
    fSource: e('來源', '출처', 'Source'),
    fPurpose: e('用途', '용도', 'Purpose'),
    fPriority: e('優先級', '우선순위', 'Priority'),
    fStream: e('流式', '스트리밍', 'Streaming'),
    fStatus: e('結果', '결과', 'Result'),
    fStatusCode: e('狀態碼', '상태 코드', 'Status code'),
    fErrorCode: e('錯誤碼', '오류 코드', 'Error code'),
    fDuration: e('耗時', '소요 시간', 'Duration'),
    fTtft: e('首字時延', '첫 토큰 지연', 'Time to first token'),
    fQueueWait: e('排隊等待', '대기 시간', 'Queue wait'),
    fAttempts: e('嘗試次數', '시도 횟수', 'Attempts'),
    fInput: e('輸入 Token', '입력 토큰', 'Input tokens'),
    fOutput: e('輸出 Token', '출력 토큰', 'Output tokens'),
    fCached: e('快取 Token', '캐시 토큰', 'Cached tokens'),
    fCacheWrite: e('快取寫入 Token', '캐시 쓰기 토큰', 'Cache write tokens'),
    fImages: e('圖片數', '이미지 수', 'Images'),
    fListPrice: e('使用者原價', '사용자 정가', 'List price'),
    fDiscount: e('優惠金額', '할인 금액', 'Discount'),
    fDiscountWhy: e('優惠原因', '할인 사유', 'Discount reason'),
    fCharge: e('使用者實扣', '사용자 실제 차감', 'Charged'),
    fUpstream: e('上遊成本', '상류 원가', 'Upstream cost'),
    fBasis: e('成本口徑', '원가 기준', 'Cost basis'),
    fBilling: e('計費方式', '과금 방식', 'Billing mode'),
    fTime: e('時間', '시각', 'Time'),
    fIp: e('IP 地址', 'IP 주소', 'IP address'),
    fDevice: e('設備 / UA', '기기 / UA', 'Device / UA'),
    fVersion: e('應用版本', '앱 버전', 'App version'),
  },

  images: {
    eyebrow: e('IMAGE OPERATIONS', 'IMAGE OPERATIONS', 'IMAGE OPERATIONS'),
    title: e('生圖記錄', '이미지 기록', 'Image records'),
    desc: e('成本統計與生成畫廊分開載入，按需切換', '비용 통계와 생성 갤러리는 따로 불러오고 필요할 때 전환해요', 'Cost stats and the gallery load separately'),
    chip: e('成本與降級可觀測', '비용과 폴백 관측', 'Cost and fallback observability'),
    refreshDates: e('按日期刷新', '날짜로 새로고침', 'Refresh by date'),
    clearDates: e('清空日期', '날짜 지우기', 'Clear dates'),
    tabsAria: e('生圖視圖切換', '이미지 보기 전환', 'Image views'),
    tabStats: e('成本統計', '비용 통계', 'Cost stats'),
    tabGallery: e('生成畫廊', '생성 갤러리', 'Gallery'),
    stImages: e('成功圖片', '성공한 이미지', 'Images generated'),
    stRequests: e('{n} 個成功請求', '성공한 요청 {n}건', '{n} successful requests'),
    stUpstream: e('已記錄上遊成本', '기록된 상류 원가', 'Recorded upstream cost'),
    stCoverage: e('成本覆蓋率 {p} · {n} 張', '원가 확인율 {p} · {n}장', 'Cost coverage {p} · {n} images'),
    stMargin: e('已知樣本毛利', '확인된 표본 마진', 'Known-sample margin'),
    stCharge: e('同批使用者實扣 {v}', '같은 표본 사용자 차감 {v}', 'Charged to users in the same sample {v}'),
    stFallbacks: e('降級成功', '폴백 성공', 'Fallback successes'),
    stFailures: e('降級率 {r} · 全鏈失敗 {n}', '폴백률 {r} · 전체 경로 실패 {n}', 'Fallback rate {r} · all-failed {n}'),
    note: e(
      '預設最近 30 天；上遊成本為空表示該渠道尚未配置採購價，不會用使用者實扣冒充成本',
      '기본은 최근 30일이에요. 상류 원가가 비어 있으면 그 채널에 매입가가 없다는 뜻이고, 사용자 차감액을 원가처럼 쓰지 않아요',
      'Defaults to the last 30 days. An empty upstream cost means no purchase price is set for the channel; user charges are never used as cost',
    ),
    dailyTitle: e('每日成本與降級', '일별 비용과 폴백', 'Daily cost and fallback'),
    dailyRange: e('{start} 至 {end} · 瀏覽器本地時區的自然日 · 上遊成本均為 USD', '{start} ~ {end} · 브라우저 현지 시간대 기준 일 · 상류 원가는 모두 USD', '{start} to {end} · calendar days in your browser time zone · upstream cost in USD'),
    refreshStats: e('刷新統計', '통계 새로고침', 'Refresh stats'),
    cDate: e('日期', '날짜', 'Date'),
    cImagesRequests: e('圖片 / 請求', '이미지 / 요청', 'Images / requests'),
    cCoverage: e('成本覆蓋', '원가 확인', 'Cost coverage'),
    cUpstream: e('上遊成本', '상류 원가', 'Upstream cost'),
    cMargin: e('已知毛利', '확인된 마진', 'Known margin'),
    cFallback: e('降級成功', '폴백 성공', 'Fallback ok'),
    cAllFailed: e('全鏈失敗', '전체 경로 실패', 'All failed'),
    cProvider: e('最終渠道 / 模型', '최종 채널 / 모델', 'Final channel / model'),
    cImages: e('圖片', '이미지', 'Images'),
    cQuote: e('採購報價（已配置）', '매입 단가 (설정됨)', 'Purchase price (configured)'),
    cPerImage: e('實際成本/張', '실제 원가/장', 'Actual cost/image'),
    cKnownCost: e('已知成本', '확인된 원가', 'Known cost'),
    cTakeover: e('降級承接', '폴백 인수', 'Fallback takeover'),
    emptyDaily: e('該日期範圍暫無生圖記錄', '이 기간에는 이미지 기록이 없어요', 'No image records in this range'),
    emptyProviders: e('該日期範圍暫無渠道資料', '이 기간에는 채널 데이터가 없어요', 'No channel data in this range'),
    loadingTab: e('切換到此標籤後載入', '이 탭으로 전환하면 불러와요', 'Loads when you open this tab'),
    statsFailed: e('統計載入失敗：{msg}', '통계를 불러오지 못했어요: {msg}', 'Stats failed to load: {msg}'),
    coverageOf: e('覆蓋 {p}', '확인 {p}', 'cover {p}'),
    notConfigured: e('未配置', '미설정', 'Not set'),
    register: e('去渠道模型庫登記', '채널 모델 라이브러리에서 등록', 'Register in channel models'),
    freeDeal: e('合作免費', '제휴 무료', 'Free by agreement'),
    perImage: e('{v} / 張', '{v} / 장', '{v} / image'),
    tokenPrice: e('輸入 {i} · 輸出 {o} / 百萬 Token', '입력 {i} · 출력 {o} / 100만 토큰', 'in {i} · out {o} / 1M tokens'),
    onlyOther: e('沒有按張或輸入輸出價', '장당 가격과 입출력 가격이 없어요', 'No per-image or in/out price'),
    discountNote: e('折扣 {d}', '할인 {d}', 'discount {d}'),
    cnyNote: e('人民幣', '위안화', 'CNY'),
    disabledNote: e('已停用', '사용 중지', 'disabled'),
    unknown: e('未知', '미확인', 'Unknown'),
    notRecorded: e('帳單未記錄', '청구에 기록 없음', 'Not recorded'),
    costTitleKnown: e('成本覆蓋 {c} · 已知樣本毛利 {m}', '원가 확인 {c} · 확인된 표본 마진 {m}', 'Cost coverage {c} · known-sample margin {m}'),
    costTitleQuoted: e('渠道模型庫已配置採購報價，但這些請求發生時帳單沒有記錄成本（報價可能是後補的），歷史帳單不會自動回填', '채널 모델 라이브러리에 매입 단가는 있지만 이 요청들이 처리될 때 원가가 기록되지 않았어요. 단가를 나중에 넣었을 수 있고 지난 청구는 자동으로 채우지 않아요', 'A purchase price is set, but cost was not recorded when these requests ran (it may have been added later). Past records are not backfilled'),
    costTitleMissing: e('渠道模型庫沒有這條路由的採購報價，請到「渠道模型庫」登記', '채널 모델 라이브러리에 이 라우트의 매입 단가가 없어요. 「채널 모델 라이브러리」에서 등록해 주세요', 'No purchase price for this route. Register it in the channel model library'),
    phUser: e('按帳號或 Key ID...', '계정 또는 키 ID로 찾기...', 'Account or key ID...'),
    phModel: e('按模型...', '모델로 찾기...', 'Model...'),
    phPrompt: e('按提示詞關鍵字...', '프롬프트 키워드로 찾기...', 'Prompt keyword...'),
    stAll: e('全部狀態', '전체 상태', 'All statuses'),
    stSuccess: e('成功', '성공', 'Success'),
    stFailed: e('失敗', '실패', 'Failed'),
    clearFilters: e('清空篩選', '필터 초기화', 'Clear filters'),
    exportJsonl: e('匯出 JSONL', 'JSONL 내보내기', 'Export JSONL'),
    exportTip: e('匯出當前篩選條件下的記錄為 JSONL 文件（含保留期內的提示詞）', '현재 필터에 맞는 기록을 JSONL로 내보내요 (보관 기간 안의 프롬프트 포함)', 'Export records matching the filters as JSONL, including retained prompts'),
    galleryTitle: e('生成畫廊', '생성 갤러리', 'Gallery'),
    galleryCopy: e('點選任意記錄查看請求詳情與原圖；成本按請求圖片數分攤到單張', '기록을 누르면 요청 상세와 원본 이미지를 볼 수 있어요. 비용은 요청 이미지 수로 나눠 장당으로 계산해요', 'Click a record for request details and the image; cost is split evenly across the images of a request'),
    totalBadge: e('共 {n} 條', '총 {n}건', '{n} records'),
    noPreview: e('無預覽', '미리보기 없음', 'No preview'),
    noPreviewWhy: e('未保存正文', '본문 미저장', 'Body not kept'),
    previewFailed: e('載入失敗', '불러오기 실패', 'Load failed'),
    noPrompt: e('(無 prompt)', '(프롬프트 없음)', '(no prompt)'),
    costPerImage: e('{v}/張', '{v}/장', '{v}/image'),
    costUnknown: e('成本未知', '원가 미확인', 'Cost unknown'),
    httpOf: e('HTTP {n}', 'HTTP {n}', 'HTTP {n}'),
    failedShort: e('生成失敗', '생성 실패', 'Generation failed'),
    emptyGallery: e('暫無生圖記錄', '이미지 기록이 없어요', 'No image records'),
    galleryFailed: e('生圖記錄暫時無法載入', '이미지 기록을 지금은 불러올 수 없어요', 'Image records cannot be loaded right now'),
    reload: e('重新載入', '다시 불러오기', 'Reload'),
    dTitle: e('生圖詳情', '이미지 상세', 'Image details'),
    dTitleFailed: e('生圖失敗詳情', '이미지 생성 실패 상세', 'Image failure details'),
    dAccountName: e('帳號', '계정', 'Account'),
    dAccountId: e('帳號 ID', '계정 ID', 'Account ID'),
    dEndUser: e('終端使用者', '최종 사용자', 'End user'),
    dModel: e('模型', '모델', 'Model'),
    dChannel: e('渠道', '채널', 'Channel'),
    dUpstreamModel: e('上遊模型', '상류 모델', 'Upstream model'),
    dCostPer: e('上遊成本 / 張', '상류 원가 / 장', 'Upstream cost / image'),
    dCostUnknown: e('未知（未配置採購價）', '미확인 (매입 단가 없음)', 'Unknown (no purchase price)'),
    dCostTotal: e('整單上遊成本', '요청 전체 상류 원가', 'Upstream cost of the request'),
    dCostNote: e('{n} 張均分 · {basis}', '{n}장으로 나눔 · {basis}', 'split over {n} images · {basis}'),
    dBasisNone: e('來源未記錄', '출처 기록 없음', 'source not recorded'),
    dCharge: e('使用者整單實扣', '사용자 요청 전체 차감', 'Charged for the request'),
    dTime: e('時間', '시각', 'Time'),
    dDuration: e('耗時', '소요 시간', 'Duration'),
    dQueueWait: e('排隊等待', '대기 시간', 'Queue wait'),
    dRequestId: e('Request ID', 'Request ID', 'Request ID'),
    dStatusCode: e('狀態碼', '상태 코드', 'Status code'),
    dIp: e('IP', 'IP', 'IP'),
    dDevice: e('設備 ID', '기기 ID', 'Device ID'),
    dSource: e('來源', '출처', 'Source'),
    dVersion: e('版本', '버전', 'Version'),
    dError: e('錯誤資訊', '오류 메시지', 'Error'),
    dPrompt: e('提示詞（Prompt）', '프롬프트', 'Prompt'),
    dPromptNone: e('提示詞已超過保存期限被清理，或這條請求產生在統一保存正文之前', '프롬프트는 보관 기간이 지나 정리됐거나, 본문을 일괄 저장하기 전에 만들어진 기록이에요', 'The prompt expired and was purged, or the request predates universal body retention'),
    dPreviewNone: e('這條記錄沒有保留可預覽的圖片', '이 기록에는 미리 볼 수 있는 이미지가 남아 있지 않아요', 'No retained image to preview for this record'),
    dPreviewNav: e('第 {i} / {n} 張', '{i} / {n}번째 장', 'Image {i} of {n}'),
    dCopyError: e('複製錯誤資訊', '오류 메시지 복사', 'Copy error'),
    dCopyPrompt: e('複製提示詞', '프롬프트 복사', 'Copy prompt'),
    dCopyRequest: e('複製 Request ID', 'Request ID 복사', 'Copy request ID'),
    dDownload: e('下載圖片', '이미지 내려받기', 'Download image'),
    dCopyLink: e('複製連結', '링크 복사', 'Copy link'),
  },

  search: {
    eyebrow: e('SEARCH RECORDS', 'SEARCH RECORDS', 'SEARCH RECORDS'),
    title: e('搜尋記錄', '검색 기록', 'Search records'),
    desc: e('網路搜尋與網頁讀取的逐次呼叫：誰搜了什麼、哪個引擎出的結果、花了多少、成本多少', '웹 검색과 웹페이지 읽기 호출을 건별로 보여줘요. 누가 무엇을 검색했고 어느 엔진이 결과를 냈는지, 비용은 얼마인지 확인해요', 'Every web search and page read: who searched what, which engine answered, and what it cost'),
    chip: e('成功與失敗都在這裡', '성공과 실패 모두 여기에 있어요', 'Successes and failures both appear here'),
    stTotal: e('呼叫記錄', '호출 기록', 'Calls'),
    stFailed: e('失敗次數', '실패 횟수', 'Failures'),
    stFailedSub: e('所有引擎都失敗，不扣費', '모든 엔진이 실패하면 과금하지 않아요', 'All engines failed, not billed'),
    stFallback: e('回退次數', '폴백 횟수', 'Fallbacks'),
    stFallbackSub: e('首選引擎失敗後由備用引擎出結果', '우선 엔진이 실패하면 예비 엔진이 결과를 내요', 'Backup engine answered after the primary failed'),
    stCost: e('上遊成本', '상류 원가', 'Upstream cost'),
    stCostSub: e('收入 {v}', '수익 {v}', 'Revenue {v}'),
    today: e('今天', '오늘', 'Today'),
    last7: e('近 7 天', '최근 7일', 'Last 7 days'),
    last30: e('近 30 天', '최근 30일', 'Last 30 days'),
    phUser: e('使用者 ID', '사용자 ID', 'User ID'),
    phKeyword: e('搜尋詞或網址', '검색어 또는 URL', 'Query or URL'),
    allEngines: e('全部引擎', '전체 엔진', 'All engines'),
    allStatus: e('全部狀態', '전체 상태', 'All statuses'),
    stOk: e('成功', '성공', 'Success'),
    stFail: e('失敗', '실패', 'Failed'),
    stFb: e('已回退', '폴백됨', 'Fell back'),
    stNoResults: e('新聞無結果', '뉴스 결과 없음', 'No news results'),
    exclInternal: e('排除內部帳號', '내부 계정 제외', 'Exclude internal accounts'),
    query: e('查詢', '조회', 'Search'),
    reset: e('清空', '초기화', 'Reset'),
    note: e(
      '本網關目前沒有聯網搜尋與網頁讀取介面，所以這裡沒有記錄，也不會生成演示資料。接入聯網搜尋後，這一頁會按下面的列顯示每次呼叫的引擎、狀態、耗時、花費與上遊成本',
      '이 게이트웨이에는 아직 웹 검색과 웹페이지 읽기 API가 없어서 기록이 없고, 시연용 데이터도 만들지 않아요. 웹 검색을 연결하면 아래 열로 호출마다 엔진, 상태, 소요 시간, 비용, 상류 원가를 보여줘요',
      'The gateway has no web search or page-read API yet, so there are no records and no demo data. Once search is connected this page shows the engine, status, duration, charge and upstream cost of each call in the columns below',
    ),
    cTime: e('時間', '시각', 'Time'),
    cUser: e('使用者', '사용자', 'User'),
    cCapability: e('能力', '기능', 'Capability'),
    cContent: e('內容', '내용', 'Content'),
    cStatus: e('狀態', '상태', 'Status'),
    cEngine: e('引擎', '엔진', 'Engine'),
    cDuration: e('耗時', '소요 시간', 'Duration'),
    cCharge: e('花費', '비용', 'Charge'),
    cUpstream: e('上遊成本', '상류 원가', 'Upstream cost'),
    cAction: e('操作', '작업', 'Action'),
    empty: e('暫無搜尋記錄', '검색 기록이 없어요', 'No search records'),
    unsupported: e('未接入聯網搜尋', '웹 검색이 연결되지 않았어요', 'Web search is not connected'),
  },
}
